/**
 * Feature-collection diffing for MapLibre's ``GeoJSONSource.updateData`` —
 * feature spec ``ui-track-load-headroom`` §2.4; REQ-UI-194.
 *
 * # Why
 *
 * A GeoJSON source fed by ``setData`` re-serialises, re-transfers and
 * re-tiles every feature it holds on every call — so a live layer that
 * reposts its whole collection per store change pays O(features) per event
 * even when one feature moved. ``updateData`` (MapLibre ≥ 4; the project is
 * on 6.x) takes a diff instead: features to add, ids to remove, and per-id
 * updates carrying a new geometry and changed properties. The source then
 * re-tiles only what changed. This module turns "the collection I would have
 * posted" into that diff, so the layer components keep building collections
 * (their tested, pure path) and the impl posts the difference.
 *
 * # How a change is detected
 *
 * Each feature gets a cheap **signature** string: the coordinate count, a
 * checksum over every coordinate (so a corrected middle vertex is seen, not
 * only a changed end), and the properties — either verbatim or through a
 * caller-supplied bucketing function, so a continuously varying property
 * (a track's ``age_fraction``, painted through a ``step`` expression) only
 * produces an update when its painted bucket changes. The previous
 * signatures live with the map the source belongs to (the impl keeps them in
 * a ``WeakMap``) and are replaced by the ones this pass returns.
 *
 * Feature identity is the caller's id selector; the source must be created
 * with the matching ``promoteId`` so MapLibre keys the diff the same way.
 *
 * Cross-references:
 *   - map/object-track-layer-impl.ts, map/detection-layer-impl.ts (the users).
 *   - REQ-UI-194 (render cost independent of event rate / member count).
 */
import type { GeoJSONSourceDiff } from "maplibre-gl";

/** Previous-pass signatures by feature id. */
export type FeatureSignatures = ReadonlyMap<string, string>;

/** The result of one diff pass. */
export interface FeatureCollectionDiff {
  /** The ``updateData`` argument, or ``null`` when nothing changed. */
  readonly diff: GeoJSONSourceDiff | null;
  /** The signatures to hand to the next pass (the previous map when unchanged). */
  readonly signatures: FeatureSignatures;
}

type Position = GeoJSON.Position;

function coordinateChecksum(geometry: GeoJSON.Geometry): { count: number; sum: number } {
  let count = 0;
  let sum = 0;
  const visit = (position: Position): void => {
    count += 1;
    // A position-weighted sum so a swapped pair of vertices is seen too.
    sum += (position[0] + position[1] * 1.000001) * count;
  };
  switch (geometry.type) {
    case "Point":
      visit(geometry.coordinates);
      break;
    case "LineString":
    case "MultiPoint":
      for (const position of geometry.coordinates) visit(position);
      break;
    case "Polygon":
    case "MultiLineString":
      for (const ring of geometry.coordinates) for (const position of ring) visit(position);
      break;
    case "MultiPolygon":
      for (const polygon of geometry.coordinates)
        for (const ring of polygon) for (const position of ring) visit(position);
      break;
    case "GeometryCollection":
      for (const member of geometry.geometries) {
        const inner = coordinateChecksum(member);
        count += inner.count;
        sum += inner.sum;
      }
      break;
  }
  return { count, sum };
}

function signatureOf<G extends GeoJSON.Geometry, P>(
  feature: GeoJSON.Feature<G, P>,
  bucket: ((properties: P) => string) | undefined,
): { geometry: string; properties: string } {
  const { count, sum } = coordinateChecksum(feature.geometry);
  const properties =
    bucket === undefined ? JSON.stringify(feature.properties) : bucket(feature.properties);
  return { geometry: `${count}:${sum}`, properties };
}

/**
 * Diff ``next`` against the previous pass's ``signatures``.
 *
 * @param signatures - What the source currently holds, from the last pass
 *     (an empty map for a fresh source).
 * @param next - The collection the layer would have posted.
 * @param idOf - The feature's id — the property the source's ``promoteId``
 *     names.
 * @param bucket - Optional property bucketing: the returned string stands
 *     for the properties in the signature, so a change inside a bucket is
 *     not an update. Omitted ⇒ the properties compare verbatim.
 */
export function diffFeatureCollection<G extends GeoJSON.Geometry, P>(
  signatures: FeatureSignatures,
  next: GeoJSON.FeatureCollection<G, P>,
  idOf: (feature: GeoJSON.Feature<G, P>) => string,
  bucket?: (properties: P) => string,
): FeatureCollectionDiff {
  const nextSignatures = new Map<string, string>();
  const add: GeoJSON.Feature[] = [];
  const update: NonNullable<GeoJSONSourceDiff["update"]> = [];
  let changed = false;
  for (const feature of next.features) {
    const id = idOf(feature);
    const { geometry, properties } = signatureOf(feature, bucket);
    const signature = `${geometry}|${properties}`;
    nextSignatures.set(id, signature);
    const previous = signatures.get(id);
    if (previous === undefined) {
      add.push(feature as GeoJSON.Feature);
      changed = true;
      continue;
    }
    if (previous === signature) {
      continue;
    }
    changed = true;
    const geometryChanged = !previous.startsWith(`${geometry}|`);
    const entry: NonNullable<GeoJSONSourceDiff["update"]>[number] = { id };
    if (geometryChanged) {
      entry.newGeometry = feature.geometry;
    }
    const props = feature.properties as Record<string, unknown> | null;
    if (props !== null) {
      entry.addOrUpdateProperties = Object.entries(props).map(([key, value]) => ({ key, value }));
    }
    update.push(entry);
  }
  const remove: string[] = [];
  for (const id of signatures.keys()) {
    if (!nextSignatures.has(id)) {
      remove.push(id);
      changed = true;
    }
  }
  if (!changed) {
    return { diff: null, signatures };
  }
  const diff: GeoJSONSourceDiff = {};
  if (remove.length > 0) diff.remove = remove;
  if (add.length > 0) diff.add = add;
  if (update.length > 0) diff.update = update;
  return { diff, signatures: nextSignatures };
}
